import React from "react";
import { Form } from "react-bootstrap";
import { Controller, Control } from "react-hook-form";
import CreatableSelect from "react-select/creatable";
import { ISelectOption } from "../interfaces/shared/ISelectOption";

interface Select2Props<T> {
  name?: string;
  control?: Control<any>;

  value?: T | T[] | null;
  onChange?: (value: T | T[] | null) => void;

  options: ISelectOption<T>[];
  label?: string;
  placeholder?: string;
  isMulti?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  error?: string;
}

const customStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: "8px",
    padding: "0.35rem 0",
    borderColor: state.isFocused ? "#dbe0e5" : "#dbe0e5",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(147, 7, 31, 0.25)" : "none",
    "&:hover": {
      borderColor: "#d21034",
    },
  }),

  multiValue: (styles: any) => ({
    ...styles,
    backgroundColor: "#d21034",
    borderRadius: "8px",
    padding: "2px 8px",
  }),
  multiValueLabel: (styles: any) => ({
    ...styles,
    backgroundColor: "#d21034",
    color: "white",
    borderRadius: "8px",
  }),
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: "white",
    backgroundColor: "#d21034",
    borderRadius: "8px",
    ":hover": {
      backgroundColor: "#93071f",
      color: "white",
    },
  }),
};

const Select2 = <T,>({
  name,
  control,

  value: externalValue,
  onChange: externalOnChange,

  options,
  label,
  placeholder,
  isMulti = false,
  isDisabled = false,
  isClearable = true,
  error,
}: Select2Props<T>) => {
  const resolveValue = (value: any) =>
    isMulti
      ? options.filter((opt) => value?.includes(opt.value))
      : (options.find((opt) => opt.value === value) ?? null);

  const handleChange = (selected: any, onChange?: (v: any) => void) => {
    const newValue = isMulti
      ? selected
        ? (selected as ISelectOption<T>[]).map((i) => i.value)
        : []
      : selected
        ? (selected as ISelectOption<T>).value
        : null;

    onChange?.(newValue);
    externalOnChange?.(newValue);
  };

  const select = (value: any, onChange?: (v: any) => void) => (
    <CreatableSelect
      isMulti={isMulti}
      isDisabled={isDisabled}
      isClearable={isClearable}
      options={options}
      value={resolveValue(value)}
      placeholder={placeholder}
      styles={customStyles}
      isValidNewOption={() => false}
      onChange={(selected) => handleChange(selected, onChange)}
    />
  );

  return (
    <Form.Group className="mb-3">
      {label && <Form.Label>{label}</Form.Label>}

      {control && name ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) => select(field.value, field.onChange)}
        />
      ) : (
        select(externalValue, externalOnChange)
      )}

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </Form.Group>
  );
};

export default Select2;
